import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Pause,
  SkipForward,
  Flag,
  Check,
  X,
  Timer,
  Trophy,
  Sparkles,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SECTIONS, buildAllQuestions } from "@/data/questions";
import { loadCustomQuestions } from "@/lib/customQuestions";
import { loadNotes, getNote } from "@/lib/notes";
import AITerminal from "@/components/AITerminal";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

const DURATIONS = [
  { value: "1800", label: "30 minutes" },
  { value: "3600", label: "60 minutes (officiel)" },
  { value: "5400", label: "90 minutes" },
];

export default function Exam() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("setup"); // setup | running | done
  const [section, setSection] = useState("all");
  const [duration, setDuration] = useState("3600");
  const [count, setCount] = useState("15");
  const [withAI, setWithAI] = useState(false);
  const [customQs, setCustomQs] = useState([]);
  const [notes, setNotes] = useState({});

  useEffect(() => {
    setCustomQs(loadCustomQuestions());
    setNotes(loadNotes());
  }, []);

  const allQuestions = useMemo(() => buildAllQuestions(customQs), [customQs]);

  const [pool, setPool] = useState([]);
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState({}); // qid -> "ok" | "ko" | "skip"
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);

  const candidates = useMemo(() => {
    return section === "all"
      ? allQuestions
      : allQuestions.filter((q) => q.sectionId === section);
  }, [section, allQuestions]);

  const start = () => {
    const n = Math.min(Math.max(parseInt(count, 10) || 10, 1), candidates.length);
    const picked = shuffle(candidates).slice(0, n);
    setPool(picked);
    setIdx(0);
    setResults({});
    setSecondsLeft(parseInt(duration, 10));
    setPaused(false);
    setShowAI(false);
    startedAtRef.current = Date.now();
    setPhase("running");
  };

  const finish = () => {
    setPhase("done");
    if (timerRef.current) clearInterval(timerRef.current);
  };

  useEffect(() => {
    if (phase !== "running" || paused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          setPhase("done");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, paused]);

  const current = pool[idx];
  const answered = Object.keys(results).length;

  const mark = (status) => {
    if (!current) return;
    setResults((r) => ({ ...r, [current.id]: status }));
    setShowAI(false);
    if (idx + 1 >= pool.length) {
      finish();
    } else {
      setIdx(idx + 1);
    }
  };

  const totalSec = parseInt(duration, 10);
  const pctTime = totalSec === 0 ? 0 : Math.round(((totalSec - secondsLeft) / totalSec) * 100);
  const lowTime = secondsLeft < 60 && phase === "running";

  // Setup screen
  if (phase === "setup") {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 bg-grid">
        <header className="border-b border-zinc-800 px-5 md:px-8 py-4 flex items-center gap-4">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-sky-400 transition-colors uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
          <h1 className="font-display text-lg md:text-xl font-bold flex items-center gap-2">
            <Timer className="w-5 h-5 text-sky-400" /> Mode examen
          </h1>
        </header>

        <main className="px-5 md:px-8 py-10 max-w-2xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-sky-400 font-bold mb-3">
            Simulation oral · jury TP AIS
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-black mb-3">
            Prêt à passer ton oral ?
          </h2>
          <p className="text-sm text-zinc-400 mb-8 leading-relaxed">
            Une session chronométrée tire au sort un nombre de questions
            définies dans une section. Tu marques chaque question comme
            « réussie », « à revoir » ou « passée ». À la fin, tu obtiens un
            bilan complet.
          </p>

          <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-5">
            <Field label="Durée">
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger
                  data-testid="exam-duration"
                  className="bg-zinc-950 border-zinc-800 rounded-none h-10"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Section">
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger
                  data-testid="exam-section"
                  className="bg-zinc-950 border-zinc-800 rounded-none h-10"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les sections</SelectItem>
                  {SECTIONS.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label={`Nombre de questions (max ${candidates.length})`}
            >
              <input
                data-testid="exam-count"
                type="number"
                min="1"
                max={candidates.length}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 px-3 h-10 text-sm focus:outline-none focus:border-sky-500"
              />
            </Field>

            <div className="flex items-center gap-2">
              <input
                data-testid="exam-with-ai"
                id="with-ai"
                type="checkbox"
                checked={withAI}
                onChange={(e) => setWithAI(e.target.checked)}
                className="accent-sky-500 w-4 h-4"
              />
              <label
                htmlFor="with-ai"
                className="text-sm text-zinc-300 cursor-pointer"
              >
                Activer la réponse IA pendant l'examen (mode entraînement)
              </label>
            </div>
          </div>

          <button
            data-testid="exam-start"
            onClick={start}
            disabled={candidates.length === 0}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-zinc-950 font-bold px-6 py-3 text-sm transition-colors uppercase tracking-wider"
          >
            <Play className="w-4 h-4" />
            Démarrer la session
          </button>
        </main>
      </div>
    );
  }

  // Done screen
  if (phase === "done") {
    const ok = Object.values(results).filter((v) => v === "ok").length;
    const ko = Object.values(results).filter((v) => v === "ko").length;
    const skip = Object.values(results).filter((v) => v === "skip").length;
    const unanswered = pool.length - answered;
    const total = pool.length;
    const score = total === 0 ? 0 : Math.round((ok / total) * 100);
    const usedTime = totalSec - secondsLeft;

    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 bg-grid">
        <header className="border-b border-zinc-800 px-5 md:px-8 py-4 flex items-center gap-4">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-sky-400 transition-colors uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
          <h1 className="font-display text-lg md:text-xl font-bold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-emerald-400" /> Bilan de session
          </h1>
        </header>

        <main className="px-5 md:px-8 py-10 max-w-3xl mx-auto space-y-8">
          <div
            data-testid="exam-score"
            className="bg-zinc-900 border border-zinc-800 p-8 text-center"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Score
            </div>
            <div className="font-display text-7xl font-black text-emerald-400">
              {score}%
            </div>
            <div className="text-sm text-zinc-400 mt-3">
              {ok} maîtrisée{ok > 1 ? "s" : ""} sur {total} · temps utilisé{" "}
              {fmt(usedTime)} / {fmt(totalSec)}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
            <Stat label="Maîtrisées" value={ok} color="text-emerald-400" />
            <Stat label="À revoir" value={ko} color="text-amber-400" />
            <Stat label="Passées" value={skip} color="text-zinc-400" />
            <Stat label="Non vues" value={unanswered} color="text-zinc-500" />
          </div>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
              Détail des questions
            </div>
            {pool.map((q, i) => {
              const r = results[q.id];
              return (
                <div
                  key={q.id}
                  data-testid={`exam-result-${q.id}`}
                  className="bg-zinc-900/60 border border-zinc-800 px-4 py-3 flex items-start gap-3"
                >
                  <div className="text-xs font-mono text-zinc-600 w-10 shrink-0 pt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-sky-400 font-bold mb-1">
                      {q.sectionShort}
                    </div>
                    <div className="text-sm text-zinc-100">{q.text}</div>
                  </div>
                  <ResultBadge r={r} />
                </div>
              );
            })}
          </div>

          <div className="flex gap-3 flex-wrap">
            <button
              data-testid="exam-restart"
              onClick={() => setPhase("setup")}
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold px-5 py-2.5 text-sm transition-colors uppercase tracking-wider"
            >
              <Play className="w-4 h-4" />
              Nouvelle session
            </button>
            <button
              data-testid="exam-go-antiseche"
              onClick={() => navigate("/antiseche")}
              className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 px-5 py-2.5 text-sm transition-colors uppercase tracking-wider text-zinc-200"
            >
              Voir l'antisèche
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Running screen
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 bg-grid flex flex-col">
      <header className="sticky top-0 z-20 bg-zinc-950/85 backdrop-blur border-b border-zinc-800 px-5 md:px-8 py-4">
        <div className="flex items-center gap-4 flex-wrap">
          <button
            data-testid="exam-abort"
            onClick={() => setPhase("setup")}
            className="text-zinc-500 hover:text-zinc-200 text-sm uppercase tracking-widest"
          >
            Abandonner
          </button>
          <div
            data-testid="exam-progress"
            className="text-[10px] uppercase tracking-[0.25em] text-zinc-400"
          >
            Question {idx + 1} / {pool.length} · {answered} répondues
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <button
              data-testid="exam-pause"
              onClick={() => setPaused((p) => !p)}
              className="text-zinc-400 hover:text-sky-400 transition-colors"
            >
              {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <div
              data-testid="exam-timer"
              className={`font-mono text-2xl font-bold tabular-nums ${
                lowTime ? "text-red-400 animate-pulse" : "text-sky-400"
              }`}
            >
              {fmt(secondsLeft)}
            </div>
          </div>
        </div>
        <div className="mt-3 h-1 bg-zinc-800 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              lowTime ? "bg-red-500" : "bg-sky-500"
            }`}
            style={{ width: `${pctTime}%` }}
          />
        </div>
      </header>

      <main className="flex-1 px-5 md:px-8 py-8 max-w-3xl mx-auto w-full">
        {current && (
          <article className="bg-zinc-900 border border-zinc-800 px-6 md:px-10 py-8">
            <div className="flex items-center justify-between mb-6">
              <span className="text-[10px] uppercase tracking-[0.3em] text-sky-400 font-bold">
                {current.sectionShort}
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                {current.groupTitle}
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-3">
              Question
            </div>
            <p
              data-testid="exam-question"
              className="font-display text-2xl md:text-3xl leading-snug mb-8"
            >
              {current.text}
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                data-testid="exam-mark-ok"
                onClick={() => mark("ok")}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold px-4 py-2.5 text-sm transition-colors uppercase tracking-wider"
              >
                <Check className="w-4 h-4" />
                Maîtrisée
              </button>
              <button
                data-testid="exam-mark-ko"
                onClick={() => mark("ko")}
                className="inline-flex items-center gap-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/40 font-bold px-4 py-2.5 text-sm transition-colors uppercase tracking-wider"
              >
                <X className="w-4 h-4" />À revoir
              </button>
              <button
                data-testid="exam-mark-skip"
                onClick={() => mark("skip")}
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-4 py-2.5 text-sm transition-colors uppercase tracking-wider"
              >
                <SkipForward className="w-4 h-4" />
                Passer
              </button>
              {withAI && !showAI && (
                <button
                  data-testid="exam-show-ai"
                  onClick={() => setShowAI(true)}
                  className="ml-auto inline-flex items-center gap-2 border border-sky-500/40 text-sky-400 hover:bg-sky-500/10 px-4 py-2.5 text-sm transition-colors uppercase tracking-wider"
                >
                  <Sparkles className="w-4 h-4" />
                  Voir réponse IA
                </button>
              )}
            </div>

            {showAI && (
              <AITerminal
                question={current.text}
                questionId={current.id}
                sectionTitle={current.sectionTitle}
                groupTitle={current.groupTitle}
                note={getNote(notes, current.id)}
                context={`${current.sectionTitle} — ${current.groupTitle}`}
                onClose={() => setShowAI(false)}
              />
            )}
          </article>
        )}
      </main>

      <Dialog open={paused}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Session en pause</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Le timer est suspendu. Reprends quand tu es prêt.
            </DialogDescription>
          </DialogHeader>
          <button
            data-testid="exam-resume"
            onClick={() => setPaused(false)}
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold px-4 py-2 text-sm uppercase tracking-wider"
          >
            <Play className="w-4 h-4" />
            Reprendre
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        {label}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-zinc-950 px-5 py-4 flex flex-col gap-2">
      <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        {label}
      </span>
      <span className={`font-display text-3xl font-bold ${color}`}>{value}</span>
    </div>
  );
}

function ResultBadge({ r }) {
  if (r === "ok") {
    return (
      <span className="inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-400 px-2 py-1 text-[10px] font-mono border border-emerald-500/20 uppercase tracking-widest shrink-0">
        <Check className="w-3 h-3" /> OK
      </span>
    );
  }
  if (r === "ko") {
    return (
      <span className="inline-flex items-center gap-1 bg-amber-500/10 text-amber-400 px-2 py-1 text-[10px] font-mono border border-amber-500/20 uppercase tracking-widest shrink-0">
        <Flag className="w-3 h-3" /> À revoir
      </span>
    );
  }
  if (r === "skip") {
    return (
      <span className="inline-flex items-center gap-1 bg-zinc-800/50 text-zinc-400 px-2 py-1 text-[10px] font-mono border border-zinc-700 uppercase tracking-widest shrink-0">
        Passée
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-zinc-800/30 text-zinc-600 px-2 py-1 text-[10px] font-mono border border-zinc-800 uppercase tracking-widest shrink-0">
      Non vue
    </span>
  );
}
